import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast, Toaster } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ClipboardPlus,
  FileHeart,
  FileText,
  ImageIcon,
  Loader2,
  MessagesSquare,
  Sparkles,
  Trash2,
  Upload,
  Download,
  ArrowRight,
  Calendar,
} from "lucide-react";
import CompartirExpedienteCard from "@/components/CompartirExpedienteCard";

const MAX_MB = 10;

function humanSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function Dashboard() {
  const { user } = useAuth();

  const [estado, setEstado] = useState(null); // {complete, exists}
  const [labs, setLabs] = useState([]);
  const [consultas, setConsultas] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [estadoResp, labsResp, consultasResp] = await Promise.all([
        api.get("/historia-clinica/estado"),
        api.get("/laboratorios"),
        api.get("/consultas"),
      ]);
      setEstado(estadoResp.data);
      setLabs(labsResp.data);
      setConsultas(consultasResp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`El archivo excede ${MAX_MB} MB.`);
      return;
    }
    setUploadingFile(file.name);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/laboratorios", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLabs((prev) => [data, ...prev]);
      toast.success(`Archivo cargado: ${data.nombre}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const openLab = async (id, nombre) => {
    try {
      const { data } = await api.post(`/laboratorios/${id}/url-firmada`);
      // Build the URL from REACT_APP_BACKEND_URL so it always points at the
      // public host, not the internal cluster host that request.base_url may
      // resolve to behind a Kubernetes ingress. The token is self-contained
      // auth so the backend `url` field is treated as a hint only.
      const publicUrl = `${process.env.REACT_APP_BACKEND_URL}/api/laboratorios/descarga/${data.token}`;
      window.open(publicUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/laboratorios/${deleteTarget.id}`);
      setLabs((prev) => prev.filter((l) => l.id !== deleteTarget.id));
      toast.success("Archivo eliminado.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) upload(file);
  };

  const firstName = user?.nombre_completo?.split(" ")[0] || "persona";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
        {/* Hero */}
        <section className="rounded-xl border border-border bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-widest text-[#2563EB] flex items-center gap-2">
              <span>Panel de la persona usuaria</span>
              {user?.role === "admin" && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-[#0F2942] text-white px-2 py-0.5 text-[10px] tracking-widest normal-case font-medium"
                  data-testid="admin-mode-chip"
                >
                  Modo administrador
                </span>
              )}
            </div>
            <h1 className="font-display text-3xl font-semibold text-[#0F2942] tracking-tight mt-1">
              Hola, {firstName}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Captura tu historia clínica cifrada y sube tus laboratorios para consultas futuras
              más seguras.
            </p>
          </div>
          <Link
            to={estado?.complete ? "/consulta/nueva" : "/historia-clinica"}
            className="inline-flex items-center gap-2 rounded-lg bg-[#2563EB] text-white text-sm font-medium px-4 py-2.5 hover:bg-[#1e4fd0] transition-colors"
            data-testid="dashboard-new-consulta"
          >
            <ClipboardPlus size={14} /> Nueva consulta IA — $39
          </Link>
        </section>

        {/* Historia clínica banner */}
        <section className="mt-6" data-testid="historia-banner-section">
          {estado === null ? (
            <div className="rounded-xl border border-border bg-white p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Verificando historia clínica…
            </div>
          ) : estado.complete ? (
            <div
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between"
              data-testid="historia-complete-banner"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="text-sm font-semibold text-emerald-900">
                    Historia clínica completa
                  </div>
                  <div className="text-xs text-emerald-800 mt-0.5">
                    Puedes consultar el motor clínico con tu perfil cifrado.
                  </div>
                </div>
              </div>
              <Link to="/historia-clinica">
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-white"
                  data-testid="historia-edit-btn"
                >
                  Editar
                </Button>
              </Link>
            </div>
          ) : (
            <div
              className="rounded-xl border border-amber-300 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between"
              data-testid="historia-incomplete-banner"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="text-sm font-semibold text-amber-900">
                    Completa tu historia clínica
                  </div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    Es requisito antes de generar una consulta con IA. Toma menos de 3 minutos.
                  </div>
                </div>
              </div>
              <Link to="/historia-clinica">
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="historia-start-btn"
                >
                  Completar ahora
                </Button>
              </Link>
            </div>
          )}
        </section>

        {/* Content grid */}
        <section className="mt-6 grid lg:grid-cols-3 gap-4">
          {/* Historia clínica card */}
          <div
            className="rounded-xl border border-border bg-white p-6 flex flex-col justify-between min-h-[220px] lg:col-span-1"
            data-testid="historia-clinica-card"
          >
            <div>
              <div className="w-9 h-9 rounded-lg bg-muted grid place-items-center text-[#0F2942]">
                <FileHeart size={18} />
              </div>
              <h3 className="font-display text-lg font-semibold text-[#0F2942] mt-4">
                Historia Clínica
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Datos demográficos, alergias, enfermedades crónicas, medicación, condiciones
                especiales y hábitos. Cifrado AES-256-GCM.
              </p>
            </div>
            <div className="mt-5 flex items-center gap-2">
              <Link to="/historia-clinica">
                <Button variant="outline" size="sm" data-testid="historia-open-btn">
                  {estado?.exists ? "Ver / editar" : "Comenzar"}
                </Button>
              </Link>
              {estado?.exists && estado?.updated_at ? (
                <span className="text-[11px] text-muted-foreground">
                  Actualizada: {new Date(estado.updated_at).toLocaleDateString("es-MX")}
                </span>
              ) : null}
            </div>
          </div>

          {/* Laboratorios card */}
          <div
            className="rounded-xl border border-border bg-white p-6 flex flex-col lg:col-span-2 min-h-[220px]"
            data-testid="mis-laboratorios-card"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="w-9 h-9 rounded-lg bg-muted grid place-items-center text-[#0F2942] mb-3">
                  <FileText size={18} />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#0F2942]">
                  Mis Laboratorios
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md">
                  PDF, JPG o PNG · máx {MAX_MB} MB. Se guarda en almacenamiento privado y solo se
                  descarga con URL firmada válida 5 min.
                </p>
              </div>
            </div>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`mt-4 rounded-lg border-2 border-dashed p-4 transition-colors ${
                dragOver ? "border-[#2563EB] bg-[#2563EB]/5" : "border-border bg-muted/40"
              }`}
              data-testid="lab-dropzone"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="text-sm text-muted-foreground">
                  {uploadingFile ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin" /> Subiendo {uploadingFile}…
                    </span>
                  ) : (
                    <>Arrastra un archivo aquí o selecciona uno</>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="lab-file-input"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!!uploadingFile}
                  data-testid="lab-upload-btn"
                >
                  <Upload size={14} /> Seleccionar archivo
                </Button>
              </div>
            </div>

            {/* File list */}
            <ul className="mt-4 space-y-2" data-testid="lab-list">
              {busy && labs.length === 0 && (
                <li className="text-xs text-muted-foreground flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Cargando…
                </li>
              )}
              {!busy && labs.length === 0 && (
                <li className="text-xs text-muted-foreground">
                  Aún no has subido archivos.
                </li>
              )}
              {labs.map((lab) => (
                <li
                  key={lab.id}
                  className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2"
                  data-testid={`lab-item-${lab.id}`}
                >
                  <div className="w-7 h-7 rounded bg-muted grid place-items-center text-[#0F2942]">
                    {lab.tipo === "application/pdf" ? (
                      <FileText size={14} />
                    ) : (
                      <ImageIcon size={14} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {lab.nombre}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {lab.tipo} · {humanSize(lab.tamano)} ·{" "}
                      {new Date(lab.fecha).toLocaleDateString("es-MX")}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openLab(lab.id, lab.nombre)}
                    data-testid={`lab-view-${lab.id}`}
                  >
                    <Download size={14} /> Ver
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteTarget(lab)}
                    aria-label={`Eliminar ${lab.nombre}`}
                    data-testid={`lab-delete-${lab.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </li>
              ))}
            </ul>
          </div>

          {/* Consultas — real list */}
          <div
            className="rounded-xl border border-border bg-white p-6 flex flex-col justify-between min-h-[220px] lg:col-span-3"
            data-testid="mis-consultas-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-muted grid place-items-center text-[#0F2942] shrink-0">
                  <MessagesSquare size={18} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-semibold text-[#0F2942]">
                    Mis Consultas
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-lg">
                    Repositorio de consultas con IA. Cada una cita la GPC utilizada y respeta las
                    reglas OTC de COFEPRIS.
                  </p>
                </div>
              </div>
              <Link to={estado?.complete ? "/consulta/nueva" : "/historia-clinica"}>
                <Button
                  size="sm"
                  className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
                  data-testid="mis-consultas-new-btn"
                >
                  <ClipboardPlus size={14} /> Nueva consulta
                </Button>
              </Link>
            </div>

            <ul className="mt-4 space-y-2" data-testid="mis-consultas-list">
              {consultas.length === 0 && !busy && (
                <li className="text-xs text-muted-foreground">
                  Aún no tienes consultas. Cuando termines una, aparecerá aquí.
                </li>
              )}
              {consultas.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center gap-3 rounded-md border border-border bg-white px-3 py-2"
                  data-testid={`consulta-row-${c.id}`}
                >
                  <span
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      c.estado === "completada"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : c.estado === "procesando"
                        ? "border-blue-200 bg-blue-50 text-blue-800"
                        : c.estado === "requiere_info"
                        ? "border-indigo-200 bg-indigo-50 text-indigo-800"
                        : c.estado === "pendiente_pago"
                        ? "border-amber-200 bg-amber-50 text-amber-800"
                        : "border-red-200 bg-red-50 text-red-800"
                    }`}
                    data-testid={`consulta-estado-chip-${c.estado}`}
                  >
                    {c.estado === "procesando" && (
                      <Loader2 size={10} className="animate-spin" />
                    )}
                    {c.estado === "completada" && <CheckCircle2 size={10} />}
                    {c.estado === "requiere_info"
                      ? "necesita más información"
                      : c.estado.replace("_", " ")}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground truncate">
                      {c.resumen || "(sin resumen)"}
                    </div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                      <Calendar size={10} />
                      {new Date(c.created_at).toLocaleDateString("es-MX")}
                      {c.gpc_codes?.length > 0 && (
                        <span className="font-mono ml-1">· {c.gpc_codes.join(", ")}</span>
                      )}
                    </div>
                  </div>
                  <Link to={`/consulta/${c.id}`}>
                    <Button variant="ghost" size="sm" data-testid={`consulta-open-${c.id}`}>
                      Ver <ArrowRight size={12} />
                    </Button>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Compartir expediente con médicos (Fase 5.9) */}
        <CompartirExpedienteCard />
      </main>
      <Footer />
      <Toaster position="top-right" richColors />

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="lab-delete-dialog">
          <DialogHeader>
            <DialogTitle>Eliminar archivo</DialogTitle>
            <DialogDescription>
              ¿Seguro que quieres eliminar
              {deleteTarget ? ` "${deleteTarget.nombre}"` : ""}? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              data-testid="lab-delete-cancel"
            >
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={doDelete}
              data-testid="lab-delete-confirm"
            >
              Eliminar definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
